import React, { useState, forwardRef, useRef, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useActiveEditor } from '../../context/ActiveEditorContext';
import { useSettings } from '../../context/SettingsContext';
import hiveService from '../../services/hive';
import { postsApi } from '../../services/api';
import CommentCard from '../CommentCard/CommentCard';
import InlineCommentNode from '../InlineCommentNode/InlineCommentNode';
import ComposerPreviewModal from '../ComposerPreviewModal/ComposerPreviewModal';
import RichEditor from '../RichEditor';
import { htmlToMarkdown } from '../RichEditor/richEditorUtils';
import { buildOptimisticReplyBody } from '../../utils/optimisticReply';
import { buildOptimisticComment, buildOptimisticCommentUpsertPayload } from '../../utils/optimisticComment';
import { buildFlexDetailPath, getReplyCharacterLimit } from '../../utils/postUrls';
import './CommentsSection.css';


const CommentsSection = forwardRef(({
  comments,
  loading,
  onCommentPosted,
  onRetryOptimistic,
  onOptimisticComment,
  onOptimisticReply,
  onOptimisticCommentUpdate,
  post,
  replyBoxRef
}, ref) => {
  // flexdetail-style recursive render function
  const REPLY_DEPTH_LIMIT = parseInt(process.env.REACT_APP_REPLY_DEPTH_LIMIT, 10) || 4;

  const { commentDisplayMode } = useSettings();
  const [expandedComments, setExpandedComments] = useState(new Set());

  const renderCommentNode = (comment, level = 0, parentExpanded = false) => {
    if (commentDisplayMode === 'inline') {
      return (
        <InlineCommentNode
          key={`${comment.author}-${comment.permlink}`}
          comment={comment}
          depth={level}
          maxDepth={REPLY_DEPTH_LIMIT}
          postAuthor={post.author}
          postPermlink={post.permlink}
          onOptimisticReply={onOptimisticReply}
          onOptimisticCommentUpdate={onOptimisticCommentUpdate}
        />
      );
    }

    const key = `${comment.author}/${comment.permlink}`;
    const commentExpanded = expandedComments.has(key);
    const hasReplies = comment.replies && comment.replies.length > 0;
    const isAtLimit = level >= REPLY_DEPTH_LIMIT;
    const isInExtra = parentExpanded;
    const isAtExtraLimit = parentExpanded && level >= REPLY_DEPTH_LIMIT + 2;

    const handleToggle = () => {
      if (commentExpanded) {
        setExpandedComments(prev => { const s = new Set(prev); s.delete(key); return s; });
      } else {
        setExpandedComments(prev => new Set([...prev, key]));
      }
    };

    const showChildren = hasReplies && (
      commentExpanded ||
      (isInExtra && !isAtExtraLimit)
    );

    return (
      <div key={key} className={`comment-node level level-${level}`}>
        <CommentCard
          post={comment}
          showActions={true}
          isDetailView={false}
          showCommentToggle={false}
          hideCommentButton={false}
          onExpandDeeper={hasReplies ? handleToggle : undefined}
          mobileLimitPath={isAtLimit && !isInExtra && !commentExpanded && hasReplies ? buildFlexDetailPath(comment.author, comment.permlink) : undefined}
          onCommentPosted={onCommentPosted}
          onOptimisticComment={(oc) => {
            setExpandedComments(prev => new Set([...prev, `${comment.author}/${comment.permlink}`]));
            if (onOptimisticReply) {
              onOptimisticReply(comment.author, comment.permlink, oc);
            } else if (onOptimisticComment) {
              onOptimisticComment(oc);
            }
          }}
          onOptimisticCommentUpdate={onOptimisticCommentUpdate}
          onRetryOptimistic={onRetryOptimistic}
        />
        {showChildren && (
          <div className="nested-replies">
            {comment.replies.map(c2 => renderCommentNode(c2, level + 1, isInExtra || (isAtLimit && commentExpanded)))}
          </div>
        )}
        {isAtLimit && !isInExtra && !commentExpanded && hasReplies && (
          <Link to={buildFlexDetailPath(comment.author, comment.permlink)} className="view-deeper-link show-on-mobile-only">
            Show deeper replies →
          </Link>
        )}
        {isAtExtraLimit && hasReplies && (
          <Link to={buildFlexDetailPath(comment.author, comment.permlink)} className="view-deeper-link">
            Show deeper replies →
          </Link>
        )}
      </div>
    );
  };
  const { user, isAuthenticated } = useAuth();
  const { activeEditorId, openEditor, closeEditor } = useActiveEditor();
  const [sortBy, setSortBy] = useState('newest');
  const [showComments, setShowComments] = useState(true);
  const [replyText, setReplyText] = useState('');
  const [replyHtml, setReplyHtml] = useState('');
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const richEditorRef = useRef(null);
  const editorId = `article-reply:${post.author}/${post.permlink}`;
  const showArticleReply = activeEditorId === editorId;
  const replyCharLimit = getReplyCharacterLimit(post);
  const parentAuthor = String(post?.parentAuthor || post?.parent_author || '').trim();
  const depthValue = Number(post?.depth);
  const isCurrentPostArticle = Number.isFinite(depthValue) ? depthValue === 0 : !parentAuthor;
  const replyButtonLabel = isCurrentPostArticle ? 'Reply to Article' : 'Reply';
  const replyPlaceholder = isCurrentPostArticle
    ? 'Write a reply to this article...'
    : 'Write a reply to this comment...';

  // Total comments count (prefer authoritative `post.children` when available)

  // note: recursion renders all nested replies explicitly, so we do not
  // rely on expand/toggle state and therefore avoid duplicate rendering.
  const countNested = (nodes = []) => {
    let total = 0;
    nodes.forEach(n => {
      total += 1;
      if (n.replies && n.replies.length) total += countNested(n.replies);
    });
    return total;
  };
  const totalCommentsCount = typeof post?.children === 'number' ? post.children : countNested(comments || []);

  const scheduleCommentConfirmationCheck = useCallback((author, permlink) => {
    const checkDelays = [20000, 60000];

    checkDelays.forEach((delay, index) => {
      setTimeout(async () => {
        const exists = await hiveService.checkPostExists(author, permlink);
        if (exists) {
          if (onOptimisticCommentUpdate) {
            onOptimisticCommentUpdate(permlink, { optimistic: false, optimisticStatus: 'confirmed' });
          }
          return;
        }
        if (index === checkDelays.length - 1) {
          if (onOptimisticCommentUpdate) {
            onOptimisticCommentUpdate(permlink, { optimisticStatus: 'failed', optimisticError: 'Not found on-chain yet. Retry?' });
          }
        }
      }, delay);
    });
  }, [onOptimisticCommentUpdate]);

  const handleReplyChange = (html) => {
    setReplyHtml(html);
    const markdown = htmlToMarkdown(html);
    setReplyText(markdown);
  };

  const handlePostReply = async () => {
    const { normalizedReply, optimisticReplyBody } = buildOptimisticReplyBody({
      replyHtml,
      replyText,
      username: user?.username
    });

    if (!isAuthenticated) {
      alert('Please login to comment');
      return;
    }

    if (!normalizedReply.trim()) {
      alert('Please enter a comment');
      return;
    }

    setIsPosting(true);
    let optimisticPermlink = '';
    try {
      const permlink = hiveService.generatePermlinkForReply(post.author, post.rootAuthor);
      optimisticPermlink = permlink;
      const createdAt = new Date().toISOString();
      const jsonMetadata = hiveService.buildCommentMetadata(user.username, normalizedReply, post.author, post.rootAuthor || post.author, permlink);

      const optimisticComment = buildOptimisticComment({
        author: user.username,
        permlink,
        body: optimisticReplyBody,
        createdAt,
        jsonMetadata,
        targetPost: { author: post.author, permlink: post.permlink },
        rootAuthor: post.rootAuthor || post.author,
        depth: 1
      });

      if (onOptimisticComment) {
        onOptimisticComment(optimisticComment);
      }

      await hiveService.createComment(
        user.username,
        post.author,
        post.permlink,
        normalizedReply,
        post.rootAuthor || post.author,
        { permlink }
      );

      await postsApi.upsertOptimisticShortPost(buildOptimisticCommentUpsertPayload({
        author: user.username,
        permlink,
        body: optimisticReplyBody,
        jsonMetadata,
        targetPost: { author: post.author, permlink: post.permlink },
        createdAt
      }));

      setReplyText('');
      setReplyHtml('');
      setPreviewOpen(false);
      closeEditor(editorId);
      alert('Comment posted successfully!');
      scheduleCommentConfirmationCheck(user.username, permlink);
    } catch (error) {
      console.error('Error posting comment:', error);
      if (onOptimisticCommentUpdate && optimisticPermlink) {
        onOptimisticCommentUpdate(optimisticPermlink, { optimisticStatus: 'failed', optimisticError: error.message || 'Broadcast failed' });
      }
      alert('Failed to post comment: ' + error.message);
    } finally {
      setIsPosting(false);
    }
  };

  const previewReplyRawBody = replyHtml && replyHtml.trim() ? htmlToMarkdown(replyHtml) : replyText;
  const previewReplyBody = user
    ? hiveService.appendUserSignatureForContext(previewReplyRawBody, user.username, 'comment')
    : previewReplyRawBody;
  const previewReplyPost = user ? {
    author: user.username,
    permlink: 'preview-article-reply',
    body: previewReplyBody,
    created: new Date().toISOString(),
    json_metadata: JSON.stringify({ app: 'flexus' }),
    parentAuthor: post.author,
    parentPermlink: post.permlink,
    rootAuthor: post.rootAuthor || post.author,
    depth: 1,
    votes: { total: 0, up: 0 },
    replies: 0,
  } : null;

  if (!showComments) {
    return (
      <section className="comments-section" ref={ref}>
        <div className="comments-toggle-bar">
          <button 
            className="comments-toggle-btn"
            onClick={() => setShowComments(true)}
          >
            💬 Show {totalCommentsCount} Comments
          </button>
        </div>
      </section>
    );
  }

  // Sort comments
  const sortedComments = [...comments].sort((a, b) => {
    switch (sortBy) {
      case 'newest':
        return new Date(b.created) - new Date(a.created);
      case 'oldest':
        return new Date(a.created) - new Date(b.created);
      case 'votes':
        return (b.net_votes || 0) - (a.net_votes || 0);
      default:
        return 0;
    }
  });

  return (
    <section className="comments-section" ref={ref}>
      <div className="comments-header">
        <div className="comments-title-row">
          <h3 className="comments-title">💬 Comments ({totalCommentsCount})</h3>
          <button 
            className="comments-close-btn"
            onClick={() => setShowComments(false)}
            title="Hide comments"
          >
            Hide
          </button>
        </div>

        {totalCommentsCount > 0 && (
          <div className="comments-controls">
            <label className="sort-label">Sort by:</label>
            <select 
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="sort-select"
            >
              <option value="newest">Newest</option>
              <option value="oldest">Oldest</option>
              <option value="votes">Most Votes</option>
            </select>
          </div>
        )}
      </div>

      {/* Reply Box */}
      {isAuthenticated && (
        <div className="article-reply-box">
          {!showArticleReply ? (
            <button
              type="button"
              className="create-post-btn"
              onClick={() => openEditor(editorId)}
            >
              {replyButtonLabel}
            </button>
          ) : (
            <>
          <RichEditor
            ref={replyBoxRef || richEditorRef}
            value={replyHtml}
            onChange={handleReplyChange}
            onSubmit={handlePostReply}
            placeholder={replyPlaceholder}
            maxLength={replyCharLimit}
            toolbar={true}
          />
          <div className="reply-actions">
            <div className="reply-meta-actions">
              {replyCharLimit && <span className="char-count">{replyText.length}/{replyCharLimit}</span>}
              <button
                type="button"
                className="preview-create-btn"
                onClick={() => setPreviewOpen(true)}
                disabled={!replyText.trim() || isPosting}
              >
                Preview
              </button>
            </div>
            <div className="reply-submit-actions">
              <button 
                  className="reply-cancel-btn"
                  onClick={() => {
                    setPreviewOpen(false);
                    closeEditor(editorId);
                    setReplyText('');
                    setReplyHtml('');
                  }}
                disabled={isPosting}
              >
                Cancel
              </button>
              <button 
                onClick={handlePostReply}
                disabled={isPosting || !replyText.trim()}
                className="reply-submit-btn"
              >
                {isPosting ? 'Posting...' : 'Post Reply'}
              </button>
            </div>
          </div>

          <ComposerPreviewModal
            isOpen={previewOpen}
            title="Preview Reply"
            content={previewReplyBody}
            previewNode={previewReplyPost ? <CommentCard post={previewReplyPost} showActions={false} showCommentToggle={false} hideCommentButton={true} /> : null}
            author={user?.username || post.author}
            focusTargetRef={replyBoxRef || richEditorRef}
            submitLabel="Post Reply"
            isSubmitting={isPosting}
            onClose={() => setPreviewOpen(false)}
            onKeepWriting={() => {
              setPreviewOpen(false);
              window.setTimeout(() => {
                (replyBoxRef?.current || richEditorRef.current)?.focus?.();
              }, 0);
            }}
            onSubmit={handlePostReply}
          />
            </>
          )}
        </div>
      )}

      {post?.children > 0 && (!comments || comments.length === 0) ? (
        <div className="comments-placeholder">
          <div className="comment-skeleton">
            <div className="skeleton-avatar" />
            <div className="skeleton-lines">
              <div className="skeleton-line short" />
              <div className="skeleton-line" />
              <div className="skeleton-line tiny" />
            </div>
          </div>

          <div className="comment-skeleton">
            <div className="skeleton-avatar small" />
            <div className="skeleton-lines">
              <div className="skeleton-line" />
              <div className="skeleton-line short" />
            </div>
          </div>

          <div className="comment-skeleton">
            <div className="skeleton-avatar small" />
            <div className="skeleton-lines">
              <div className="skeleton-line" />
              <div className="skeleton-line tiny" />
            </div>
          </div>

          <div className="loading-note">Loading comments…</div>
        </div>
      ) : totalCommentsCount === 0 ? (
        <div className="empty-comments">
          <p>No comments yet. Be the first to comment!</p>
        </div>
      ) : (
        <div className="comments-list loading-state" data-state={loading ? 'loading' : 'idle'}>
          {sortedComments.map((comment, index) => (
            renderCommentNode(comment, 0)
          ))}
        </div>
      )}
    </section>
  );
});

CommentsSection.displayName = 'CommentsSection';

export default CommentsSection;
